import { useState } from "react";
import { Loader2, Tags, Wrench, CheckCircle2, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "../../lib/api";
import { useWorkspace } from "../../store/WorkspaceContext";

const KIND = { rename: ["Rename", "#4c9ffe"], retype: ["Window type", "#f0c945"], straighten: ["Straighten", "#f5a524"] };

// Owner: one click fixes house names, window types, small panes filed as doors and tilted centre glass — no need to ask.
export const NameFixesPanel = () => {
  const { dispatch } = useWorkspace();
  const [scan, setScan] = useState(null);
  const [busy, setBusy] = useState(false);
  const [fixing, setFixing] = useState(null); // "all" | vehicle id
  const [done, setDone] = useState({});
  const run = async () => {
    setBusy(true); setDone({});
    try { setScan(await api.nameFixesScan()); } catch (e) { toast.error(apiError(e, "Check failed")); } finally { setBusy(false); }
  };
  const fix = async (vehicleId = "") => {
    setFixing(vehicleId || "all");
    try {
      const r = await api.nameFixesApply(vehicleId);
      const d = {}; r.changes.forEach((c) => { d[c.id] = c; });
      setDone((x) => ({ ...x, ...d }));
      dispatch({ type: "LIBRARY_CHANGED" });
      toast.success(r.fixed ? `${r.fixed} template${r.fixed === 1 ? "" : "s"} fixed` : "Nothing needed changing");
    } catch (e) { toast.error(apiError(e, "Fix failed")); }
    setFixing(null);
  };
  const open = (vid) => dispatch({ type: "SET", patch: { loadVehicleId: vid, dialog: null } });
  const left = (scan?.vehicles || []).map((v) => ({ ...v, issues: v.issues.filter((i) => !done[i.id]) })).filter((v) => v.issues.length);
  return (
    <div className="space-y-2" data-testid="name-fixes-panel">
      <div className="glass-row flex flex-wrap items-center gap-3 p-3">
        <Tags className="h-4 w-4 text-[#3ecf8e]" />
        <div className="min-w-0 flex-1 text-[12px]">
          <div className="font-medium text-[#e8e9ec]">Names &amp; tilt check</div>
          <div className="text-[11px] text-[#9aa0a8]">Finds templates off the house names (Back Glass · Front Door · Rear Door · Rear Quarter · Front Quarter · Windshield · Sunroof), a small fixed pane filed as a door, a window typed wrong, and centre glass sitting crooked — and fixes them in one click.</div>
        </div>
        <button data-testid="name-fixes-scan-btn" onClick={run} disabled={busy} className="glass-btn flex h-8 items-center gap-1.5 px-3 text-[11px]">{busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Tags className="h-3.5 w-3.5 text-[#3ecf8e]" />} {scan ? "Check again" : "Check names & tilt"}</button>
        {left.length > 0 && <button data-testid="name-fixes-all-btn" onClick={() => fix()} disabled={Boolean(fixing)} className="btn-red flex h-8 items-center gap-1.5 px-3 text-[11px]">{fixing === "all" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Wrench className="h-3.5 w-3.5" />} Fix all ({left.reduce((n, v) => n + v.issues.length, 0)})</button>}
      </div>
      {scan && !busy && left.length === 0 && <div className="flex items-center gap-1.5 px-1 text-[12px] text-[#3ecf8e]" data-testid="name-fixes-clean"><CheckCircle2 className="h-4 w-4" /> Every template has its house name, the right window type and sits straight.</div>}
      {left.map((v) => (
        <div key={v.vehicle_id} className="glass-row overflow-hidden" data-testid={`name-fixes-vehicle-${v.vehicle_id}`}>
          <div className="flex items-center justify-between gap-2 border-b border-white/10 px-3 py-1.5 text-[11px] font-medium text-[#e8e9ec]">
            <span>{v.label}</span>
            <span className="flex items-center gap-2">
              <button data-testid={`name-fixes-open-${v.vehicle_id}`} onClick={() => open(v.vehicle_id)} className="flex items-center gap-1 text-[10px] text-[#9aa0a8] hover:text-white"><ExternalLink className="h-3 w-3" /> Open</button>
              <button data-testid={`name-fixes-fix-${v.vehicle_id}`} onClick={() => fix(v.vehicle_id)} disabled={Boolean(fixing)} className="glass-btn flex h-6 items-center gap-1 px-2 text-[10px]">{fixing === v.vehicle_id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Wrench className="h-3 w-3" />} Fix this vehicle</button>
            </span>
          </div>
          <ul className="divide-y divide-white/5">
            {v.issues.map((i) => (
              <li key={i.id} data-testid={`name-fixes-row-${i.id}`} className="flex flex-wrap items-center gap-2 px-3 py-1.5 text-[12px]">
                <span className="min-w-[150px] text-[#c7cbd1]">{i.name} <span className="font-mono text-[10px] text-[#6b7079]">{i.size}"</span></span>
                <span className="text-[#6b7079]">→</span>
                <span className="font-medium text-[#e8e9ec]">{i.to}{i.tilt ? <span className="ml-1 font-mono text-[10px] text-[#f5a524]">{i.tilt > 0 ? "+" : ""}{i.tilt}°</span> : null}</span>
                <span className="ml-auto flex gap-1">{i.kinds.map((k) => <span key={k} className="rounded-full px-2 py-px font-mono text-[10px]" style={{ background: `${KIND[k][1]}22`, color: KIND[k][1] }}>{KIND[k][0]}</span>)}</span>
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
};
